import { useQuery } from '@apollo/client';
import type { ChangeEvent, FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import SelectField from '@app/components/input/SelectField';
import { getLocalizationCountryCookie } from '@app/helpers/localization';
import { isNotNullish } from '@app/helpers/predicate';
import useValidationMessage from '@app/hooks/useValidationMessage';

import { AddressFormFields, AddressFormNamespaces } from '../Address.constants';
import messages from '../Address.messages';
import { sortCountries } from '../helpers';
import useStoreSwitch from '../useStoreSwitch';
import { ADDRESS_COUNTRIES } from './AddressForm.queries';
import type { AddressCountriesQuery } from './__generated__/AddressCountriesQuery';

export const CountryRegionField: FC<{
  namespace: string;
  isVirtual?: boolean;
}> = ({ namespace, isVirtual }) => {
  const { data } = useQuery<AddressCountriesQuery>(ADDRESS_COUNTRIES, {
    fetchPolicy: 'cache-first',
    ssr: false,
  });
  const {
    formState: { errors },
    setValue,
    watch,
  } = useFormContext();
  const { msg } = useValidationMessage(messages);

  const initialCountryCode = getLocalizationCountryCookie();
  const selectedCountryCode =
    (watch(`${namespace}.${AddressFormFields.Country}`) as string) ||
    initialCountryCode;

  useStoreSwitch(initialCountryCode, selectedCountryCode, namespace);

  const countries = data?.magento2?.countries?.filter(isNotNullish);
  const sortedCountries = countries && sortCountries(countries);
  const selectedCountry = sortedCountries?.find(
    (country) => country?.id === initialCountryCode,
  );

  const countriesRequireState =
    data?.magento2?.storeConfig?.countriesRequireState;
  const regions = selectedCountry?.availableRegions?.filter(isNotNullish) || [];
  const showRegions = countriesRequireState?.includes(initialCountryCode);

  return (
    <>
      <Controller
        name={`${namespace}.${AddressFormFields.Country}`}
        defaultValue={initialCountryCode}
        render={({ field: { ref, onChange, ...props } }) => (
          <SelectField
            data-testid={`${namespace}-country`}
            autoComplete="disabled"
            disabled={!isVirtual && namespace === AddressFormNamespaces.Billing}
            isValid={!errors[namespace]?.[AddressFormFields.Country]}
            formLabel={<FormattedMessage {...messages.country} />}
            options={countries?.map((country) => ({
              value: country?.id ?? undefined,
              label: country?.fullNameLocale ?? undefined,
            }))}
            onChange={(e: ChangeEvent<HTMLSelectElement>) => {
              setValue(`${namespace}.${AddressFormFields.Region}`, '');
              onChange((e?.target as HTMLSelectElement).value);
            }}
            innerRef={ref}
            {...props}
          />
        )}
        rules={{
          required: {
            value: true,
            message: msg({
              type: 'required',
              key: 'country',
            }),
          },
        }}
      />

      <Controller
        name={`${namespace}.${AddressFormFields.Region}`}
        defaultValue=""
        render={({ field: { ref, onChange, ...props } }) => (
          <SelectField
            data-testid={`${namespace}-region`}
            css={!showRegions && { display: 'none' }}
            isValid={!errors[namespace]?.[AddressFormFields.Region]}
            formLabel={<FormattedMessage {...messages.region} />}
            options={regions?.map((state) => ({
              value: state?.id ?? undefined,
              label: state.name ?? undefined,
            }))}
            innerRef={ref}
            onChange={(e: ChangeEvent<HTMLSelectElement>) =>
              onChange(Number(e.target?.value))
            }
            {...props}
          />
        )}
        rules={{
          required: {
            value: Boolean(showRegions),
            message: msg({
              type: 'required',
              key: 'region',
            }),
          },
        }}
      />
    </>
  );
};
